import { taxIdLabel, viesZiel, VIES_CACHE_STUNDEN } from "@talentkontor/domain";
import type { Api, VatCheckAntwort, VatCheckRow } from "../data";
import { date, time } from "../lib/format";
import { currentLang, t } from "../lib/sprache";

/**
 * Prüf-Chat Nachtest (03.11.2026), H: der Begriff der Nummer folgt dem Land der Nummer (Österreich „UID-Nr.“, Schweiz
 * „UID/MWST-Nr.“, sonst „USt-IdNr.“), wie das Feld beim Empfänger; englisch „VAT ID“.
 */
export function idWortVon(vatId: string | null | undefined): string {
  const v = (vatId ?? "").replace(/\s+/g, "").toUpperCase();
  const land = /^CHE/.test(v) ? "CH" : /^[A-Z]{2}/.test(v) ? (v.slice(0, 2) === "EL" ? "GR" : v.slice(0, 2)) : null;
  return taxIdLabel(land === "DE" || land === "AT" || land === "CH" || land === "LI" ? land : "DE", currentLang() === "en" ? "en" : "de");
}

/**
 * Steuerprüfung L6 (0445): Prüfung der USt-IdNr. bei VIES im Portal. Die Abfrage läuft über die Serverfunktion vat-check
 * (Api.vatCheck, nur mit Supabase); das Ergebnis steht in public.vat_checks und wird beim Festschreiben einer Rechnung mit Reverse
 * Charge (EU) am Beleg eingefroren (invoices.vat_check). Hier: Texte für Marke, Generator und Rechnung, deutsch und englisch.
 */

export type { VatCheckAntwort, VatCheckRow };

/** Eingefrorener Nachweis an der Rechnung (app.guard_vat_check) */
export interface VatCheckNachweis {
  status: "valid" | "invalid" | "unchecked" | "not_applicable";
  vat_id?: string;
  checked_at?: string;
  request_identifier?: string;
  name?: string;
  address?: string;
  confirmed?: boolean;
  reason?: "none" | "unavailable" | "stale" | "format";
}

export type ViesTon = "success" | "error" | "warning" | "neutral";

const wann = (iso: string) => `${date(iso)} ${time(iso)}`;

/** Warum diese Nummer nicht bei VIES geprüft wird; null, wenn sie geprüft wird */
export function viesNichtAnwendbar(vatId: string | null | undefined): string | null {
  const z = viesZiel(vatId);
  switch (z.art) {
    case "eu": return null;
    case "leer": return t("Keine USt-IdNr. hinterlegt.", "No VAT ID on file.");
    case "gb": return t("Nummer aus dem Vereinigten Königreich: VIES prüft seit 2021 nur noch Nordirland (XI), deshalb keine Abfrage.", "UK number: since 2021 VIES only covers Northern Ireland (XI), so no check.");
    case "ch": return t("Schweizer Nummer: nicht in VIES, keine Abfrage.", "Swiss number: not in VIES, no check.");
    case "format": return t("Die Nummer hat nicht die Form einer USt-IdNr. (Länderkennung und Nummer); bitte prüfen.", "The number does not have the form of an EU VAT ID (country code and number); please check.");
    default: return t("Keine USt-IdNr. eines EU-Mitgliedstaats: keine Abfrage bei VIES.", "Not a VAT ID of an EU member state: no VIES check.");
  }
}

/** Ist eine Prüfung noch aktuell? (gültig bzw. ungültig der letzten 24 Stunden, wie Zwischenspeicher und Festschreiben) */
export function viesAktuell(row: Pick<VatCheckRow, "status" | "checked_at">, nowMs = Date.now()): boolean {
  return row.status !== "unavailable" && nowMs - Date.parse(row.checked_at) < VIES_CACHE_STUNDEN * 3_600_000;
}

/** Stand der letzten Prüfung in einem Satz, mit Ton für die Pille */
export function viesStatusText(row: VatCheckRow | null, nowMs = Date.now()): { text: string; ton: ViesTon; kurz: string } {
  if (!row) return { ton: "neutral", kurz: t("Nicht geprüft", "Not checked"), text: t("Noch nicht bei VIES geprüft.", "Not yet checked with VIES.") };
  const am = wann(row.checked_at);
  const kennung = row.request_identifier ? t(`, Kennung der Anfrage ${row.request_identifier}`, `, request identifier ${row.request_identifier}`) : "";
  const wer = [row.name, row.address].filter(Boolean).join(", ");
  const alt = !viesAktuell(row, nowMs) && row.status === "valid"
    ? t(" Älter als 24 Stunden: vor dem endgültigen Erstellen mit Reverse Charge wird neu geprüft.", " Older than 24 hours: it is checked again before issuing with reverse charge.") : "";
  if (row.status === "valid") {
    return { ton: "success", kurz: t("Gültig", "Valid"),
      text: t(`Gültig laut VIES, geprüft am ${am}${kennung}.`, `Valid according to VIES, checked on ${am}${kennung}.`) + (wer ? t(` Laut VIES: ${wer}.`, ` According to VIES: ${wer}.`) : "") + alt };
  }
  if (row.status === "invalid") {
    return { ton: "error", kurz: t("Ungültig", "Invalid"),
      text: t(`Ungültig laut VIES, geprüft am ${am}. Reverse Charge geht nur nach ausdrücklicher Bestätigung beim endgültigen Erstellen; bitte die Nummer beim Empfänger klären.`,
        `Invalid according to VIES, checked on ${am}. Reverse charge is only possible after explicit confirmation when issuing; please clarify the number with the recipient.`) };
  }
  return { ton: "warning", kurz: t("Nicht geprüft", "Not checked"),
    text: t(`VIES war am ${am} nicht erreichbar; die Nummer gilt als nicht geprüft. Das sperrt nichts, beim endgültigen Erstellen wird erneut gefragt.`,
      `VIES could not be reached on ${am}; the number counts as not checked. Nothing is blocked; it is asked again when issuing.`) };
}

/** Ergebnis einer Abfrage als Meldung (Toast) */
export function viesErgebnisText(a: VatCheckAntwort): { text: string; alert: boolean } {
  if (a.status === "not_applicable") return { text: viesNichtAnwendbar(a.vat_id) ?? t("Keine Prüfung nötig.", "No check needed."), alert: false };
  if ("reason" in a) return { alert: true, text: t("Gerade zu viele Abfragen bei VIES; die Nummer gilt vorerst als nicht geprüft. Bitte in einigen Minuten erneut prüfen.", "Too many VIES checks right now; the number counts as not checked for now. Please check again in a few minutes.") };
  const s = viesStatusText(a.check);
  return { text: `${a.check.vat_id}: ${s.text}`, alert: a.status !== "valid" };
}

/** Eingefrorener Nachweis an der Rechnung in einem Satz; null ohne Nachweis */
export function viesNachweisText(n: VatCheckNachweis | null | undefined): string | null {
  if (!n) return null;
  const nr = n.vat_id ?? "";
  if (n.status === "valid") return t(`${idWortVon(nr)} ${nr} gültig laut VIES, geprüft am ${wann(n.checked_at!)}${n.request_identifier ? `, Kennung der Anfrage ${n.request_identifier}` : ""}.`,
    `VAT ID ${nr} valid according to VIES, checked on ${wann(n.checked_at!)}${n.request_identifier ? `, request identifier ${n.request_identifier}` : ""}.`);
  if (n.status === "invalid") return t(`${idWortVon(nr)} ${nr} ungültig laut VIES (geprüft am ${wann(n.checked_at!)}); Reverse Charge ausdrücklich bestätigt.`,
    `VAT ID ${nr} invalid according to VIES (checked on ${wann(n.checked_at!)}); reverse charge explicitly confirmed.`);
  if (n.status === "not_applicable") return t(`${idWortVon(nr)} ${nr}: keine Prüfung bei VIES (keine Nummer eines EU-Mitgliedstaats).`, `VAT ID ${nr}: no VIES check (not a VAT ID of an EU member state).`);
  const grund = n.reason === "unavailable" ? t("VIES war nicht erreichbar", "VIES could not be reached")
    : n.reason === "stale" ? t("die letzte Prüfung war älter als 24 Stunden", "the last check was older than 24 hours")
      : n.reason === "format" ? t("die Nummer hat nicht die Form einer USt-IdNr.", "the number does not have the form of an EU VAT ID")
        : t("keine Prüfung vorhanden", "no check available");
  return t(`${idWortVon(nr)} ${nr} beim endgültigen Erstellen nicht bei VIES geprüft (${grund}).`, `VAT ID ${nr} not checked with VIES when issued (${grund}).`);
}

/**
 * Vor dem Festschreiben einer Rechnung mit Reverse Charge (EU): Nummer des Empfängers bei VIES prüfen (Zwischenspeicher 24 Stunden),
 * damit die Datenbank ein frisches Ergebnis einfriert. Sperrt nie: Fehler und „nicht erreichbar“ ergeben nur einen Hinweis.
 */
export async function viesVorFestschreiben(api: Pick<Api, "vatCheck">, orgId: string, invoiceId: string): Promise<string | null> {
  if (!api.vatCheck) return null;
  try {
    const a = await api.vatCheck({ org: orgId, invoice: invoiceId });
    if (a.status === "unavailable") return t("VIES ist gerade nicht erreichbar. Die Rechnung wird mit dem Vermerk „nicht geprüft“ endgültig erstellt.", "VIES cannot be reached right now. The invoice is issued with the note “not checked”.");
    return null;
  } catch {
    return t("Die USt-IdNr. ließ sich gerade nicht bei VIES prüfen. Die Rechnung wird mit dem Vermerk „nicht geprüft“ endgültig erstellt.", "The VAT ID could not be checked with VIES just now. The invoice is issued with the note “not checked”.");
  }
}

/**
 * Prüf-Chat Nachtest (03.11.2026), Befund 14: vor dem ersten Speichern zum Festschreiben (Reverse Charge EU) die Nummer bei VIES
 * prüfen; ist sie ungültig, steht das als Zeile in der Rückfrage vor dem Festschreiben. Sperrt nie (Fehler ergeben null); die
 * ausdrückliche Bestätigung mit Vermerk an der Rechnung verlangt danach weiter die Datenbank (0445).
 */
export async function viesVorab(api: Pick<Api, "vatCheck">, orgId: string, vatId: string, brandId: string | null): Promise<string | null> {
  if (!api.vatCheck) return null;
  try {
    const a = await api.vatCheck({ org: orgId, vat_id: vatId, ...(brandId ? { brand: brandId } : {}) });
    if (a.status !== "invalid") return null;
    return t(`Die USt-IdNr. ${vatId} ist laut VIES ungültig. Ohne gültige USt-IdNr. ist Reverse Charge nicht gesichert; bitte die Nummer beim Empfänger klären. Endgültig erstellen mit Reverse Charge geht nur mit ausdrücklicher Bestätigung im nächsten Schritt.`,
      `The VAT ID ${vatId} is invalid according to VIES. Without a valid VAT ID, reverse charge is not secured; please clarify the number with the recipient. Issuing with reverse charge requires explicit confirmation in the next step.`);
  } catch {
    return null;
  }
}
